import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cart";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, subtotal } = useCart();
  const navigate = useNavigate();
  const FREE_SHIP_QTY = 2;
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const freeShipping = totalQty >= FREE_SHIP_QTY;
  const remaining = Math.max(0, FREE_SHIP_QTY - totalQty);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/30"
          onClick={() => setOpen(false)}
          data-testid="cart-overlay"
        />
      )}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full max-w-md z-50 transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        style={{ background: "var(--kaiya-bg)", borderLeft: "1px solid var(--kaiya-border)" }}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "var(--kaiya-border)" }}>
            <h3 className="font-serif-display text-2xl">Your bag</h3>
            <button data-testid="cart-close" onClick={() => setOpen(false)} className="p-2"><X size={20} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {items.length === 0 && (
              <div className="text-center py-16 opacity-60">
                <ShoppingBag size={40} className="mx-auto mb-4 opacity-50" />
                <p className="font-serif-display text-xl">Your bag is empty.</p>
                <p className="text-sm mt-2">Soft fabrics and steady ground await.</p>
              </div>
            )}
            {items.map((it, idx) => (
              <div key={`${it.product_id}-${it.variant || ""}`} className="flex gap-4" data-testid={`cart-item-${idx}`}>
                <div className="image-soft w-20 h-24 shrink-0">
                  <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="font-medium leading-tight">{it.name}</div>
                  {it.variant && <div className="text-xs opacity-60 mt-1">{it.variant}</div>}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2 border rounded-full px-2 py-1" style={{ borderColor: "var(--kaiya-border)" }}>
                      <button data-testid={`cart-qty-dec-${idx}`} onClick={() => updateQty(idx, it.quantity - 1)} className="p-1"><Minus size={12} /></button>
                      <span className="text-sm w-6 text-center">{it.quantity}</span>
                      <button data-testid={`cart-qty-inc-${idx}`} onClick={() => updateQty(idx, it.quantity + 1)} className="p-1"><Plus size={12} /></button>
                    </div>
                    <div className="text-sm font-medium">${(it.unit_price * it.quantity).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t p-6 space-y-4" style={{ borderColor: "var(--kaiya-border)" }}>
            {items.length > 0 && (
              <div
                data-testid="cart-ship-banner"
                className="text-xs px-3 py-2 rounded-full text-center"
                style={{
                  background: freeShipping ? "var(--kaiya-primary)" : "var(--kaiya-bg-2)",
                  color: freeShipping ? "white" : "var(--kaiya-primary)",
                  fontWeight: 500,
                }}
              >
                {freeShipping
                  ? "Free shipping unlocked"
                  : `Add ${remaining} more towel${remaining === 1 ? "" : "s"} for free shipping`}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-serif-display text-2xl">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs opacity-60">Shipping calculated at checkout. Free on 2+ towels, else $10 flat across Canada.</p>
            <button
              data-testid="cart-checkout-btn"
              disabled={items.length === 0}
              onClick={() => { setOpen(false); navigate("/checkout"); }}
              className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Checkout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
